import { useState } from 'react';
import {
  IMAGE_PREVIEW_MEDIUMS,
  IFRAME_PREVIEW_MEDIUMS,
  MessageMediumIcon,
} from 'modules/campaigns/lib/components/CampaignPreviewThumbnail';
import { MessageMedium } from 'constants/messageMediums';
import {
  MobilePreview,
  IN_APP_PREVIEW_WIDTH,
  IN_APP_PREVIEW_HEIGHT,
} from 'modules/missionControl/lib/components/PreviewCard';
import { ResourceIcon } from './PreviewThumbnail.utils';
import { PreviewThumbnailLoading } from './PreviewThumbnailLoading';
import { ImageWrapper, PreviewImg } from './PreviewThumbnail.styled';
import { ResourceType } from '../../types';

type PreviewThumbnailProps = {
  resourceType: ResourceType | string;
  resource?: {
    messageMedium?: string;
    templateId?: number;
  };
  previewThumbnail?: string;
  isLoading?: boolean;
};

export const PREVIEW_TEST_ID = 'preview-thumbnail';

/** Gets the preview image to show as the thumbnail for a resource (i.e. Template, Campaign, Journey, etc.)
 *  todo: consider consolidating with CampaignPreviewThumbnail for a more general usage
 *
 * Returns a preview thumbnail image for message mediums included in IMAGE_PREVIEW_MEDIUMS
 * Returns an iframe preview for message mediums included in IFRAME_PREVIEW_MEDIUMS
 * Returns an icon for all other resources
 */
const PreviewThumbnailImage = ({
  resourceType,
  resource,
  previewThumbnail,
  isLoading,
}: PreviewThumbnailProps) => {
  const [showFallbackImage, setShowFallbackImage] = useState(false);

  const { messageMedium, templateId } = resource || {};

  switch (true) {
    case isLoading:
      return <PreviewThumbnailLoading />;

    case IMAGE_PREVIEW_MEDIUMS.includes(messageMedium as MessageMedium) &&
      previewThumbnail &&
      !showFallbackImage:
      return (
        <PreviewImg
          data-test={`${messageMedium}_img`}
          alt="preview"
          onError={() => setShowFallbackImage(true)}
          src={previewThumbnail}
        />
      );

    case IFRAME_PREVIEW_MEDIUMS.includes(messageMedium as MessageMedium) &&
      !!messageMedium &&
      !!templateId:
      return (
        <MobilePreview
          templateId={templateId}
          messageMedium={messageMedium}
          previewWidth={IN_APP_PREVIEW_WIDTH}
          previewHeight={IN_APP_PREVIEW_HEIGHT}
          maxWidth={15}
        />
      );

    case !!messageMedium:
      return <MessageMediumIcon messageMedium={messageMedium as string} />;

    default:
      return <ResourceIcon resourceType={resourceType} />;
  }
};

export const PreviewThumbnail = (props: PreviewThumbnailProps) => {
  const { resource = {}, resourceType } = props;
  return (
    <ImageWrapper
      data-test={`${PREVIEW_TEST_ID}-${resource.messageMedium ? resource?.messageMedium + '-' : ''}${resourceType.toLowerCase()}`}
    >
      <PreviewThumbnailImage {...props} />
    </ImageWrapper>
  );
};
